import { useI18n } from '@i18n';
import { Button, List, Stack } from '@mui/material';
import isNil from 'lodash/isNil';
import { createContext, forwardRef, HTMLAttributes, ReactNode, useContext } from 'react';
import Scrollbar from '@/components/Scrollbar';

type ListboxState = {
  allSelected: boolean;
  onToggleSelectAll: () => void;
  extraActions?: ReactNode;
};

export const TenantAreaListboxContext = createContext<ListboxState | null>(null);

// Module-scope component: an inline ListboxComponent gets a new identity each render,
// remounting the open listbox and resetting its scroll position on every selection.
export const TenantAreaListbox = forwardRef<HTMLUListElement, HTMLAttributes<HTMLElement>>(function TenantAreaListbox(
  { children, ...props },
  ref,
) {
  const { t } = useI18n();
  const listbox = useContext(TenantAreaListboxContext);

  return (
    <List
      // The action buttons live inside the listbox; without this their clicks reach the
      // Autocomplete root and toggle the popup.
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
      }}
      sx={{ '& li': { '&:hover': { backgroundColor: 'action.hover' } } }}
      {...props}
      ref={ref}
    >
      <Scrollbar sx={{ maxHeight: 340 }}>{children}</Scrollbar>
      {isNil(listbox) ? null : (
        <Stack direction="row" alignItems="center" justifyContent="flex-start" px={2} py={1} gap={1}>
          <Button type="button" variant="text" size="small" onClick={listbox.onToggleSelectAll}>
            {listbox.allSelected ? t('General_DeselectAll') : t('General_SelectAll')}
          </Button>
          {listbox.extraActions}
        </Stack>
      )}
    </List>
  );
});
